// ═══════════════════════════════════════════════════════════════
// TemplateSamplesManager.tsx – a központi mintadokumentum-tár kezelése (csak superadmin)
//
// Az „Üres közzétételi dokumentum sablonok (ZIP)” letöltés tartalma: a 2026-os központi mappaszerkezet,
// a pontok (és al-csoportok) mappáiban az itt feltöltött minta sablonfájlokkal (docx, xlsx, …).
// Feltöltés: egyes fájlok (pont kiválasztásával vagy „1_08_…” fájlnév-előtaggal) vagy egy teljes ZIP
// (a pontot a mappanév / fájlnév eleje adja – pl. a letöltött üres mappasablon kiegészítve);
// másolás egy szervezet pontjaihoz csatolt fájlokból; pontonkénti áthelyezés és törlés.
// ═══════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient, type TemplateSampleItem } from '../../api/client';
import { Upload, RefreshCw, Trash2, Copy, Library, FileText, ChevronDown, ChevronRight, Search, Link2 } from 'lucide-react';

const kb = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} kB`);

export const TemplateSamplesManager: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const [items, setItems] = useState<TemplateSampleItem[]>([]);
  const [points, setPoints] = useState<{ pont: string; label: string; chapter: string }[]>([]);
  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [pont, setPont] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [copyFrom, setCopyFrom] = useState('');
  const [liveSource, setLiveSource] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'err'; text: string; skipped?: { name: string; reason: string }[] } | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const [r, cfg] = await Promise.all([ApiClient.getTemplateSamples().catch(() => null), ApiClient.getFolderTemplateV2().catch(() => null)]);
    if (r?.ok && r.data) { setItems(r.data.items); setPoints(r.data.points); }
    if (cfg?.ok && cfg.data) setLiveSource(cfg.data.source_tenant_id ? String(cfg.data.source_tenant_id) : '');
  }, []);
  useEffect(() => {
    load();
    ApiClient.getTenants().then((r: any) => { if (r?.ok) setTenants((r.data || []).map((t: any) => ({ id: t.id, name: t.name }))); }).catch(() => {});
  }, [load]);

  const changed = () => { load(); onChanged?.(); };

  const upload = async () => {
    if (!files.length) return;
    setBusy('upload'); setMsg(null);
    const r = await ApiClient.uploadTemplateSamples(files, pont || undefined);
    setBusy(null);
    if (!r.ok) { setMsg({ tone: 'err', text: r.error || 'A feltöltés nem sikerült' }); return; }
    const n = r.added?.length || 0; const sk = r.skipped || [];
    setMsg({ tone: sk.length ? 'warn' : 'ok', text: `${n} mintafájl felvéve${sk.length ? `, ${sk.length} kihagyva` : ''}.`, skipped: sk });
    setFiles([]); if (fileRef.current) fileRef.current.value = '';
    changed();
  };

  const copy = async () => {
    if (!copyFrom) return;
    setBusy('copy'); setMsg(null);
    const r = await ApiClient.copyTemplateSamplesFromTenant(Number(copyFrom));
    setBusy(null);
    if (!r.ok) { setMsg({ tone: 'err', text: r.error || 'A másolás nem sikerült' }); return; }
    const sk = r.skipped || [];
    setMsg({ tone: sk.length ? 'warn' : 'ok', text: `${r.added || 0} fájl átmásolva (${r.source})${sk.length ? `, ${sk.length} kihagyva` : ''}.`, skipped: sk });
    changed();
  };

  const saveLive = async () => {
    setBusy('live'); setMsg(null);
    const r = await ApiClient.setFolderTemplateV2(liveSource ? Number(liveSource) : null);
    setBusy(null);
    setMsg(r.ok ? { tone: 'ok', text: 'Mentve.' } : { tone: 'err', text: r.error || 'Nem sikerült menteni' });
    if (r.ok) changed();
  };

  const remove = async (id: string | 'all') => {
    if (id === 'all' && !confirm(`Biztosan törli a tár mind a(z) ${items.length} mintafájlját? Ez nem vonható vissza.`)) return;
    setBusy(id);
    const r = await ApiClient.deleteTemplateSample(id);
    setBusy(null);
    if (!r.ok) setMsg({ tone: 'err', text: r.error || 'A törlés nem sikerült' }); else changed();
  };

  const move = async (id: string, to: string) => {
    setBusy(id);
    const r = await ApiClient.moveTemplateSample(id, to);
    setBusy(null);
    if (!r.ok) setMsg({ tone: 'err', text: r.error || 'Az áthelyezés nem sikerült' }); else changed();
  };

  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const m = new Map<string, TemplateSampleItem[]>();
    for (const it of items) {
      if (ql && !`${it.filename} ${it.label}`.toLowerCase().includes(ql)) continue;
      if (!m.has(it.pont)) m.set(it.pont, []);
      m.get(it.pont)!.push(it);
    }
    const order = new Map(points.map((p, i) => [p.pont, i]));
    return [...m.entries()].sort((a, b) => (order.get(a[0]) ?? 9999) - (order.get(b[0]) ?? 9999));
  }, [items, points, q]);

  const toggle = (k: string) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const sel = 'px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-xs';
  const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50';

  return (
    <section aria-labelledby="tsm-title" className="p-5 rounded-2xl border border-violet-200 dark:border-violet-900/60 bg-violet-50/40 dark:bg-violet-950/10 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="tsm-title" className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Library className="w-4 h-4 text-violet-600 dark:text-violet-400" /> Központi mintadokumentum-tár <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-300">superadmin</span>
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-3xl">
            Az itt felvett minta sablonfájlok kerülnek az <strong>Üres közzétételi dokumentum sablonok (ZIP)</strong> letöltés pontmappáiba. Egy teljes ZIP is feltölthető
            (pl. a letöltött üres mappasablon kiegészítve): a pontot a mappanév vagy a fájlnév eleje adja („1.8 …”, „1_08_…”); a sablon vezérlőfájljait (adat.txt, leiras.txt …) kihagyjuk.
          </p>
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-300 text-right">
          <div><b>{items.length}</b> fájl, <b>{new Set(items.map((i) => i.pont)).size}</b> ponton</div>
          {items.length > 0 && (
            <button type="button" onClick={() => remove('all')} disabled={busy !== null} className="mt-1 text-rose-600 dark:text-rose-400 hover:underline cursor-pointer disabled:opacity-50">Összes törlése</button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-2">
          <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Feltöltés (fájlok vagy ZIP)</div>
          <select value={pont} onChange={(e) => setPont(e.target.value)} aria-label="Cél pont" className={`${sel} w-full`}>
            <option value="">Automatikus felismerés (mappanév / fájlnév eleje)</option>
            {points.map((p) => <option key={p.pont} value={p.pont}>{p.label}</option>)}
          </select>
          <input ref={fileRef} type="file" multiple aria-label="Mintafájlok"
            accept=".zip,.pdf,.doc,.docx,.xls,.xlsx,.odt,.ods,.odp,.ppt,.pptx,.rtf,.txt,.csv,.png,.jpg,.jpeg"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            className="block w-full text-xs text-slate-600 dark:text-slate-300 file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-slate-100 dark:file:bg-slate-800 file:text-slate-700 dark:file:text-slate-200" />
          <button type="button" onClick={upload} disabled={!files.length || busy !== null} className={`${btn} bg-violet-600 hover:bg-violet-500 text-white`}>
            {busy === 'upload' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {files.length ? `${files.length} fájl feltöltése` : 'Feltöltés'}
          </button>
        </div>

        <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3">
          <div className="space-y-1.5">
            <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Másolás egy szervezetből</div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">A szervezet pontjaihoz csatolt fájlok a tárba másolódnak (azonos ponton azonos nevű fájl cserélődik).</p>
            <div className="flex gap-2">
              <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} aria-label="Forrás szervezet a másoláshoz" className={`${sel} flex-1 min-w-0`}>
                <option value="">— válasszon szervezetet —</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button type="button" onClick={copy} disabled={!copyFrom || busy !== null} className={`${btn} bg-indigo-600 hover:bg-indigo-500 text-white shrink-0`}>
                {busy === 'copy' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Másolás
              </button>
            </div>
          </div>
          <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
            <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Élő kiegészítő forrás (nem kötelező)</div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">A kijelölt szervezet mindenkori csatolmányai is bekerülnek a letöltésbe (a tár fájljai elsőbbséget élveznek).</p>
            <div className="flex gap-2">
              <select value={liveSource} onChange={(e) => setLiveSource(e.target.value)} aria-label="Élő kiegészítő forrás" className={`${sel} flex-1 min-w-0`}>
                <option value="">— nincs —</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button type="button" onClick={saveLive} disabled={busy !== null} className={`${btn} border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 shrink-0`}>Mentés</button>
            </div>
          </div>
        </div>
      </div>

      {msg && (
        <div role={msg.tone === 'err' ? 'alert' : 'status'} className={`p-3 rounded-xl text-xs border ${msg.tone === 'ok' ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300' : msg.tone === 'warn' ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-200' : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900 text-rose-800 dark:text-rose-300'}`}>
          {msg.text}
          {msg.skipped && msg.skipped.length > 0 && (
            <details className="mt-1"><summary className="cursor-pointer">Kihagyott fájlok</summary>
              <ul className="list-disc ml-5 mt-1 space-y-0.5 max-h-48 overflow-y-auto">{msg.skipped.map((s, i) => <li key={i}><span className="font-mono">{s.name}</span> – {s.reason}</li>)}</ul>
            </details>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          <label className="relative block max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés fájlnévre / pontra" aria-label="Keresés a mintafájlok között"
              className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white" />
          </label>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
            {groups.map(([k, list]) => {
              const isOpen = open.has(k) || Boolean(q.trim());
              return (
                <div key={k}>
                  <button type="button" onClick={() => toggle(k)} aria-expanded={isOpen}
                    className="w-full px-3 py-2 flex items-center gap-2 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer">
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                    <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">{list[0].label}</span>
                    <span className="ml-auto text-slate-500 shrink-0">{list.length} fájl</span>
                  </button>
                  {isOpen && (
                    <ul className="pb-2">
                      {list.map((it) => (
                        <li key={it.id} className="pl-9 pr-3 py-1 flex flex-wrap items-center gap-2 text-xs">
                          <FileText className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <span className="font-medium text-slate-800 dark:text-slate-200 break-all">{it.filename}</span>
                          <span className="text-slate-400">{kb(it.size)}</span>
                          <span className="ml-auto flex items-center gap-1.5">
                            <select value={it.pont} onChange={(e) => move(it.id, e.target.value)} disabled={busy !== null} aria-label={`${it.filename} áthelyezése másik pontra`} className={`${sel} max-w-[14rem]`}>
                              {points.map((p) => <option key={p.pont} value={p.pont}>{p.label}</option>)}
                            </select>
                            <button type="button" onClick={() => remove(it.id)} disabled={busy !== null} aria-label={`${it.filename} törlése`} title="Törlés a tárból"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 cursor-pointer disabled:opacity-50">
                              {busy === it.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
            {!groups.length && <div className="px-3 py-3 text-xs text-slate-500">Nincs találat.</div>}
          </div>
        </div>
      )}
    </section>
  );
};

export default TemplateSamplesManager;
